/**
 * Scoring helpers — solo + multiplayer.
 *
 * The rulebook gives these rules for a coded sentence:
 *   +1 point for each icon interpreted correctly
 *   +1 bonus point if the entire sentence is correct
 *   -1 malus point if the entire sentence is not correct
 *
 * Comparing free-form text to a target sentence is fuzzy. We use a
 * per-icon match (against the icon's gloss + a curated synonyms list)
 * plus a sentence-correct threshold expressed as a fraction of icons
 * matched. Both behaviours scale with a `Strictness` setting so casual
 * / younger players can use a more forgiving mode.
 */

import type { CodeSentence, Turn } from "./types";
import { iconKey } from "./types";
import { getCell } from "../data/boards";
import { getSynonymsFor } from "../data/synonyms";
import type { Lang } from "../i18n";

export type Strictness = "strict" | "normal" | "easy";
export const DEFAULT_STRICTNESS: Strictness = "normal";

interface StrictnessConfig {
  /** Fraction of icons that must match for a sentence-correct bonus. */
  sentenceThreshold: number;
  /** Allow alternates from the per-icon synonym list. */
  useSynonyms: boolean;
  /** Allow prefix matching ("play" matches "playing", "ran" matches "rang"). */
  prefixMatch: boolean;
  /** Allow 1-character typos for content words ≥ 5 chars. */
  fuzzyMatch: boolean;
}

const STRICTNESS_CONFIGS: Record<Strictness, StrictnessConfig> = {
  strict: { sentenceThreshold: 0.9, useSynonyms: false, prefixMatch: false, fuzzyMatch: false },
  normal: { sentenceThreshold: 0.7, useSynonyms: true,  prefixMatch: false, fuzzyMatch: false },
  easy:   { sentenceThreshold: 0.5, useSynonyms: true,  prefixMatch: true,  fuzzyMatch: true  },
};

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokens(s: string): string[] {
  return normalize(s).split(" ").filter(Boolean);
}

/** 1-edit Levenshtein-ish check used for fuzzyMatch (easy mode). */
function within1Edit(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0, j = 0, diffs = 0;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] !== longer[j]) {
      diffs++;
      if (diffs > 1) return false;
      if (shorter.length === longer.length) i++;
      j++;
    } else {
      i++; j++;
    }
  }
  return diffs + (longer.length - j) <= 1;
}

function wordMatches(userWord: string, candidate: string, cfg: StrictnessConfig): boolean {
  if (userWord === candidate) return true;
  if (cfg.prefixMatch) {
    if (userWord.length >= 3 && candidate.startsWith(userWord)) return true;
    if (candidate.length >= 3 && userWord.startsWith(candidate)) return true;
  }
  if (cfg.fuzzyMatch && userWord.length >= 5 && candidate.length >= 5) {
    if (within1Edit(userWord, candidate)) return true;
  }
  return false;
}

function buildCandidates(
  iconKey: string,
  lang: Lang,
  gloss: string,
  compound: string | undefined,
  cfg: StrictnessConfig,
): string[] {
  const fromGloss = tokens(gloss).filter((w) => w.length >= 2);
  // Compound entries (e.g. 3B1 "Science" + 1A4 "Person" = "Scientist") — the
  // resulting compound word may be the only thing in the official translation,
  // so include it as a valid match candidate for either compound member.
  const fromCompound = compound ? tokens(compound).filter((w) => w.length >= 2) : [];
  if (!cfg.useSynonyms) return Array.from(new Set([...fromGloss, ...fromCompound]));
  const syns = getSynonymsFor(iconKey, lang).map((s) => normalize(s)).filter((w) => w.length >= 1);
  return Array.from(new Set([...fromGloss, ...fromCompound, ...syns]));
}

export interface SoloScoreBreakdown {
  iconsCorrect: number;
  iconsTotal: number;
  sentenceCorrect: boolean;
  total: number;
  perIcon: { iconKey: string; gloss: string; matched: boolean }[];
  strictness: Strictness;
}

export function scoreSolo(
  code: CodeSentence,
  lang: Lang,
  userInterpretation: string,
  strictness: Strictness = DEFAULT_STRICTNESS,
): SoloScoreBreakdown {
  const cfg = STRICTNESS_CONFIGS[strictness];
  const userTokens = tokens(userInterpretation);

  // In pro-drop languages the subject pronoun is implicit (Italian "Sogno"
  // = "I dream" with no explicit "Io"). Without this accommodation, every
  // 1st/2nd-person sentence flags the subject-pronoun icon as missed even
  // though the player's interpretation is correct.
  const proDropLangs = new Set<Lang>(["it"]);
  const isImplicitSubjectPronoun = (iconKey: string, gloss: string): boolean => {
    if (!proDropLangs.has(lang) || !cfg.useSynonyms) return false;
    if (iconKey !== "1A1" && iconKey !== "1C1") return false;
    return /^(io|tu)$/i.test(gloss.trim());
  };

  const perIcon = code.glossary.map((g) => {
    const gloss = g.gloss[lang] ?? g.gloss.en ?? "";
    const compoundText = g.compound?.[lang] ?? g.compound?.en;
    const candidates = buildCandidates(g.iconKey, lang, gloss, compoundText, cfg);
    let matched = false;
    outer: for (const uw of userTokens) {
      for (const c of candidates) {
        if (wordMatches(uw, c, cfg)) { matched = true; break outer; }
      }
    }
    // Implicit subject pronoun in pro-drop languages is always credited.
    if (!matched && isImplicitSubjectPronoun(g.iconKey, gloss)) matched = true;
    return { iconKey: g.iconKey, gloss, matched };
  });

  const iconsCorrect = perIcon.filter((p) => p.matched).length;
  const iconsTotal = perIcon.length;
  const sentenceCorrect = iconsTotal > 0 && iconsCorrect / iconsTotal >= cfg.sentenceThreshold;

  const total = iconsCorrect + (sentenceCorrect ? 1 : -1);
  return { iconsCorrect, iconsTotal, sentenceCorrect, total, perIcon, strictness };
}

/** Score a multiplayer turn against a known code. Returns points-per-player. */
export function scoreTurnCoded(
  code: CodeSentence,
  lang: Lang,
  interpretations: Record<string, string>,
  strictness: Strictness = DEFAULT_STRICTNESS,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [uid, text] of Object.entries(interpretations)) {
    out[uid] = scoreSolo(code, lang, text, strictness).total;
  }
  return out;
}

/**
 * Build a synthetic CodeSentence from turn data when the sentence wasn't
 * picked from the codebook (mode === "invent" or an AI-composed sentence).
 */
export function syntheticCodeFromTurn(turn: Turn): CodeSentence | null {
  if (!turn.sentence || turn.sentence.length === 0 || !turn.composerOwnTranslation) return null;
  const translation = turn.composerOwnTranslation;
  return {
    id: turn.codeId ?? `inv-${turn.index}`,
    iconCount: turn.sentence.length,
    tokens: turn.sentence,
    translations: { en: translation, it: translation, de: translation, fr: translation },
    glossary: turn.sentence.map((tok) => {
      const cell = getCell(tok.icon.board, tok.icon.col, tok.icon.row);
      return {
        iconKey: iconKey(tok.icon),
        pos: tok.frameColor,
        tense: tok.tense,
        plural: tok.plural,
        negation: tok.negation,
        gloss: {
          en: cell?.gloss?.en ?? translation,
          it: cell?.gloss?.it ?? translation,
          de: cell?.gloss?.de ?? translation,
          fr: cell?.gloss?.fr ?? translation,
        },
      };
    }),
  };
}
